import {type ReactNode, useCallback, useMemo, useState} from 'react'
import {View} from 'react-native'
import {type ImagePickerAsset} from 'expo-image-picker'
import {type BlobRef} from '@atproto/api'
import {AtUri} from '@atproto/api'
import {msg} from '@lingui/core/macro'
import {Trans, useLingui} from '@lingui/react/macro'

import {useAgent} from '#/state/session'
import {processVideo, type VideoAction} from '#/view/com/composer/state/video'
import * as Toast from '#/view/com/util/Toast'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {
  Check_Stroke2_Corner0_Rounded as CheckThin,
  CheckThick_Stroke2_Corner0_Rounded as Check,
} from '#/components/icons/Check'
import {PaperPlane_Stroke2_Corner0_Rounded as PaperPlane} from '#/components/icons/PaperPlane'
import {TimesLarge_Stroke2_Corner0_Rounded as X} from '#/components/icons/Times'
import {Loader} from '#/components/Loader'
import {Text} from '#/components/Typography'
import {type Slide, SLIDE_TYPE_META} from '#/types/capsule'

/**
 * The vocabulary itself lives in `#/lib/curriculum`, and is re-exported here so
 * that the publishers keep importing it from the dialog that asks the question.
 * It moved because a *published* book carries the codes, so anything that names
 * one — the feed card, for one — would otherwise have to import this dialog to
 * read a table of words.
 */
export {
  CAPSULE_ALL_SUBJECTS,
  CAPSULE_CATEGORIES,
  CAPSULE_GRADES,
  getAvailableSubjects,
} from '#/lib/curriculum'
import {
  CAPSULE_ALL_SUBJECTS as ALL_SUBJECTS,
  CAPSULE_CATEGORIES as CATEGORIES,
  CAPSULE_GRADES as GRADES,
  getAvailableSubjects,
} from '#/lib/curriculum'

export function PublishCapsuleDialog({
  control,
  title,
  description,
  slides,
  video,
  themeId,
  backgroundMusicId,
  htmlThemeUrl,
  coverStartMs,
  onSuccess,
}: {
  control: Dialog.DialogControlProps
  title: string
  description: string
  slides: Slide[]
  video: ImagePickerAsset | null
  themeId?: string
  /** The bed, stored apart from the theme; `'none'` is deliberate silence. */
  backgroundMusicId?: string
  htmlThemeUrl?: string
  /**
   * Which minute of the cover film the creator chose, when the film ran longer
   * than a capsule cover may run.
   *
   * The film is uploaded whole — cutting it would mean re-encoding a file the
   * creator never asked to change — so the *window* is what travels with the
   * capsule, and the player opens the cover there (see `#/lib/coverTrim`).
   */
  coverStartMs?: number
  onSuccess?: () => void
}) {
  return (
    <Dialog.Outer control={control} testID="publishCapsuleDialog">
      <Dialog.Handle />
      <Inner
        control={control}
        title={title}
        description={description}
        slides={slides}
        video={video}
        themeId={themeId}
        backgroundMusicId={backgroundMusicId}
        htmlThemeUrl={htmlThemeUrl}
        coverStartMs={coverStartMs}
        onSuccess={onSuccess}
      />
    </Dialog.Outer>
  )
}

function Inner({
  control,
  title,
  description,
  slides,
  video,
  themeId,
  backgroundMusicId,
  htmlThemeUrl,
  coverStartMs,
  onSuccess,
}: {
  control: Dialog.DialogControlProps
  title: string
  description: string
  slides: Slide[]
  video: ImagePickerAsset | null
  themeId?: string
  backgroundMusicId?: string
  htmlThemeUrl?: string
  coverStartMs?: number
  onSuccess?: () => void
}) {
  const t = useTheme()
  const {t: l, i18n} = useLingui()
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [selectedGrade, setSelectedGrade] = useState<string | null>(null)
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null)

  const activeStepIndex1 = useMemo(() => {
    if (!selectedCategory) return 1
    if (!selectedGrade) return 2
    if (!selectedSubject) return 3
    return 4
  }, [selectedCategory, selectedGrade, selectedSubject])
  const agent = useAgent()
  const [isPending, setPending] = useState(false)
  const [isSuccess, setSuccess] = useState(false)

  const onPublish = useCallback(async () => {
    setPending(true)
    try {
      const abortController = new AbortController()
      let videoEmbed: Record<string, unknown> | undefined = undefined

      if (video) {
        const blobRef = await new Promise<BlobRef>((resolve, reject) => {
          processVideo(
            video,
            (action: VideoAction) => {
              if (action.type === 'to_done') {
                resolve(action.blobRef)
              } else if (action.type === 'to_error') {
                reject(new Error(action.error))
              }
            },
            agent,
            agent.session!.did,
            abortController.signal,
            i18n,
          )
        })
        videoEmbed = {
          $type: 'app.bsky.embed.video',
          video: blobRef,
          aspectRatio: {
            width: video.width,
            height: video.height,
          },
        }
      }

      const now = new Date().toISOString()
      const did = agent.session!.did

      const postText = title + (description ? '\n\n' + description : '')
      const postRecord = {
        $type: 'app.bsky.feed.post',
        text: postText,
        type: 'capsule',
        createdAt: now,
        embed: videoEmbed,
      }

      // 1. Create the base post.
      const postRes = await agent.com.atproto.repo.createRecord({
        repo: did,
        collection: 'app.bsky.feed.post',
        record: postRecord,
      })

      // 2. Create the capsule record referencing the post.
      const capsuleRes = await agent.com.atproto.repo.createRecord({
        repo: did,
        collection: 'app.bsky.feed.capsule',
        record: {
          $type: 'app.bsky.feed.capsule',
          title,
          description,
          status: 'published',
          themeId: themeId ?? 'default',
          // Its own field, never folded into the theme: they are two settings
          // that are changed independently, and one must not imply the other.
          backgroundMusicId,
          // The window the cover opens on, and only when there is one: a film
          // that already fits plays from its beginning, and a stored `0` would
          // be a window nobody chose.
          ...(coverStartMs && coverStartMs > 0
            ? {coverStartMs: Math.round(coverStartMs)}
            : null),
          behaviorSettings: htmlThemeUrl ? {htmlThemeUrl} : undefined,
          slides,
          slideCount: slides.filter(s => SLIDE_TYPE_META[s.type]?.isExercise)
            .length,
          level: selectedGrade ?? undefined,
          subject: selectedSubject ?? undefined,
          post: {uri: postRes.data.uri, cid: postRes.data.cid},
          createdAt: now,
        },
      })

      // 3. Patch the post with the capsule back-ref so post -> capsule discovery works.
      //    Also embed slideCount directly so the feed can show coins without an extra fetch.
      const postRkey = new AtUri(postRes.data.uri).rkey
      const exerciseSlideCount = slides.filter(
        s => SLIDE_TYPE_META[s.type]?.isExercise,
      ).length
      await agent.com.atproto.repo.putRecord({
        repo: did,
        collection: 'app.bsky.feed.post',
        rkey: postRkey,
        record: {
          ...postRecord,
          capsule: {uri: capsuleRes.data.uri, cid: capsuleRes.data.cid},
          slideCount: exerciseSlideCount,
        },
      })

      setSuccess(true)
      Toast.show(l`Capsule published!`)
      setTimeout(() => {
        control.close()
        onSuccess?.()
      }, 1500)
    } catch (e: unknown) {
      console.error('Failed to publish capsule', e)
      const message = e instanceof Error ? e.message : undefined
      Toast.show(message || l`Failed to publish capsule`)
      setPending(false)
    }
  }, [
    agent,
    control,
    coverStartMs,
    description,
    onSuccess,
    slides,
    selectedGrade,
    selectedSubject,
    title,
    themeId,
    backgroundMusicId,
    htmlThemeUrl,
    video,
  ])

  return (
    <Dialog.ScrollableInner
      testID="publishCapsuleDialogInner"
      label={l`Publish Capsule`}
      style={[a.w_full, {maxWidth: 500}]}>
      <View style={[a.gap_2xl, a.pt_md]}>
        {/* Step 1 – Who Are You Targeting? */}
        <StepOuter>
          <StepTitle
            index={1}
            title={l`Who Are You Targeting?`}
            activeIndex1={activeStepIndex1}
          />
          {selectedCategory ? (
            <SelectedTag
              label={l(
                CATEGORIES.find(c => c.value === selectedCategory)?.label ??
                  msg`Unknown`,
              )}
              onClear={() => {
                setSelectedCategory(null)
                setSelectedGrade(null)
                setSelectedSubject(null)
              }}
            />
          ) : (
            <View style={[a.gap_sm]}>
              {CATEGORIES.map(c => (
                <OptionCard
                  key={c.value}
                  label={l(c.label)}
                  onSelect={() => setSelectedCategory(c.value)}
                />
              ))}
              <View
                style={[
                  a.flex_row,
                  a.align_center,
                  a.w_full,
                  a.px_md,
                  a.py_sm,
                  a.rounded_sm,
                  a.border,
                  [t.atoms.border_contrast_low],
                ]}>
                <Text style={[a.flex_1, a.italic, a.leading_snug]}>
                  <Trans>
                    Choose the age range your content is intended for, even if
                    it’s not a school subject. Example: Robotics for ages 12–15
                    → select College.
                  </Trans>
                </Text>
              </View>
            </View>
          )}
        </StepOuter>

        {/* Step 2 – Select the Grade Level */}
        <StepOuter>
          <StepTitle
            index={2}
            title={l`Select the Grade Level`}
            activeIndex1={activeStepIndex1}
          />
          {activeStepIndex1 >= 2 &&
            (selectedGrade ? (
              <SelectedTag
                label={l(
                  (selectedCategory ? GRADES[selectedCategory] : []).find(
                    g => g.value === selectedGrade,
                  )?.label ?? msg`Unknown`,
                )}
                onClear={() => {
                  setSelectedGrade(null)
                  setSelectedSubject(null)
                }}
              />
            ) : (
              <View style={[a.gap_sm]}>
                {(selectedCategory ? GRADES[selectedCategory] : []).map(g => (
                  <OptionCard
                    key={g.value}
                    label={l(g.label)}
                    onSelect={() => setSelectedGrade(g.value)}
                  />
                ))}
              </View>
            ))}
        </StepOuter>

        {/* Step 3 – Select the Subject or Topic */}
        <StepOuter>
          <StepTitle
            index={3}
            title={l`Select the Subject or Topic`}
            activeIndex1={activeStepIndex1}
          />
          {activeStepIndex1 >= 3 &&
            (selectedSubject ? (
              <SelectedTag
                label={l(
                  ALL_SUBJECTS.find(s => s.value === selectedSubject)?.label ??
                    msg`Unknown`,
                )}
                onClear={() => setSelectedSubject(null)}
              />
            ) : (
              <View style={[a.flex_row, a.flex_wrap, a.gap_sm]}>
                {getAvailableSubjects(selectedGrade).map(s => (
                  <Button
                    key={s.value}
                    variant="outline"
                    color="secondary"
                    size="small"
                    label={l(s.label)}
                    onPress={() => setSelectedSubject(s.value)}>
                    {}
                    <ButtonText>{l(s.label)}</ButtonText>
                  </Button>
                ))}
              </View>
            ))}
        </StepOuter>

        {/* Step 4 – Publish Your Capsule */}
        <StepOuter>
          <StepTitle
            index={4}
            title={l`Publish Your Capsule`}
            activeIndex1={activeStepIndex1}
          />
          {activeStepIndex1 === 4 && (
            <View style={[a.gap_lg]}>
              <Text style={[a.text_md, a.leading_snug]}>
                <Trans>
                  Make sure your audience matches your content so it can be
                  distributed effectively and help your community grow. You can
                  review and modify it before publishing.
                </Trans>
              </Text>
              <Button
                label={l`Publish Capsule`}
                size="large"
                variant="solid"
                color="primary"
                disabled={isPending || isSuccess}
                onPress={onPublish}>
                <ButtonText>
                  <Trans>Publish Capsule</Trans>
                </ButtonText>
                <ButtonIcon
                  icon={isSuccess ? CheckThin : isPending ? Loader : PaperPlane}
                />
              </Button>
            </View>
          )}
        </StepOuter>
      </View>
      <Dialog.Close />
    </Dialog.ScrollableInner>
  )
}

function StepOuter({children}: {children: ReactNode}) {
  return <View style={[a.gap_md, a.w_full]}>{children}</View>
}

function StepTitle({
  index,
  title,
  activeIndex1,
}: {
  index: number
  title: string
  activeIndex1: number
}) {
  const t = useTheme()
  const active = activeIndex1 === index
  const completed = activeIndex1 > index
  return (
    <View style={[a.flex_row, a.gap_sm, a.pr_3xl]}>
      <View
        style={[
          a.justify_center,
          a.align_center,
          a.rounded_full,
          a.border,
          {
            width: 24,
            height: 24,
            backgroundColor: active
              ? t.palette.primary_500
              : completed
                ? t.palette.primary_100
                : t.atoms.bg_contrast_25.backgroundColor,
            borderColor: active
              ? t.palette.primary_500
              : completed
                ? t.palette.primary_400
                : t.atoms.border_contrast_low.borderColor,
          },
        ]}>
        {completed ? (
          <Check width={12} fill={t.palette.primary_500} />
        ) : (
          <Text
            style={[
              a.font_bold,
              a.text_center,
              {
                color: active
                  ? 'white'
                  : completed
                    ? t.palette.primary_700
                    : t.atoms.text_contrast_medium.color,
                fontVariant: ['tabular-nums'],
                fontSize: 12,
                lineHeight: 24,
              },
            ]}>
            {index}
          </Text>
        )}
      </View>

      <Text
        style={[
          a.flex_1,
          a.font_bold,
          a.text_lg,
          a.leading_snug,
          active ? t.atoms.text : t.atoms.text_contrast_medium,
          {
            top: 1,
          },
        ]}>
        {title}
      </Text>
    </View>
  )
}

function OptionCard({label, onSelect}: {label: string; onSelect?: () => void}) {
  const t = useTheme()
  return (
    <Button label={label} onPress={onSelect} disabled={!onSelect}>
      {({hovered, pressed}) => (
        <View
          style={[
            a.w_full,
            a.px_md,
            a.py_sm,
            a.rounded_sm,
            a.border,
            t.atoms.bg_contrast_25,
            hovered || pressed
              ? [t.atoms.border_contrast_high]
              : [t.atoms.border_contrast_low],
          ]}>
          <Text style={[a.text_md, a.font_bold, a.leading_snug]}>{label}</Text>
        </View>
      )}
    </Button>
  )
}

function SelectedTag({label, onClear}: {label: string; onClear: () => void}) {
  const {t: l} = useLingui()
  return (
    <View style={[a.flex_row, a.align_center, a.gap_md]}>
      <View style={[a.flex_1]}>
        <OptionCard label={label} />
      </View>
      <Button
        label={l(msg`Clear selection`)}
        size="tiny"
        variant="solid"
        color="secondary"
        shape="round"
        onPress={onClear}>
        <ButtonIcon icon={X} />
      </Button>
    </View>
  )
}
